import { Button, HStack, Heading, Stack } from '@chakra-ui/react';
import { useRouter } from '@tanstack/react-router';
import { useEffect, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '$/components/common/Icon';
import { SectionHeadline } from '$/components/core/Collection/SectionHeadline';
import { NewProjectButton } from '$/components/core/Projects/components/NewProjectButton';
import { DashboardSectionRow } from '$/pages/DashboardPages/components/DashboardSectionRow';
import { ProjectGridItems } from '$/pages/DashboardPages/pages/Projects/components/ProjectGridItems';
import { type ProjectFolder } from '$/services/usecases/projects/mapper/editorProject';
import { type ProjectOverviewItem } from '$/services/usecases/projects/mapper/projectOverview';
import { useLastSeenStore } from '$/stores/useLastSeenStore';

interface Props {
  projects: ProjectOverviewItem[];
  folder: ProjectFolder;
}

export const ProjectFolderView: FC<Props> = ({ folder, projects }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const addLastSeenProjectFolder = useLastSeenStore.useAddLastProjectFolder();

  useEffect(() => {
    addLastSeenProjectFolder(folder.id);
  }, [addLastSeenProjectFolder, folder.id]);

  return (
    <Stack gap='6' overflowY='auto' w='full' p='7'>
      <HStack justify='space-between' w='full'>
        <Button
          px='0'
          leftIcon={<Icon icon='arrow_right' transform='rotate(180deg)' />}
          onClick={() => router.history.back()}
          variant='text'
        >
          {t('general.backToOverview')}
        </Button>
        <NewProjectButton folderId={folder.id} />
      </HStack>
      <Heading as='h1' mb='4' fontSize='3xl'>
        {folder.name}
      </Heading>
      <SectionHeadline
        headline={`${t('dashboard.projects.title')} (${projects.length})`}
      >
        <DashboardSectionRow>
          <ProjectGridItems items={projects} folderId={folder.id} />
        </DashboardSectionRow>
      </SectionHeadline>
    </Stack>
  );
};
